"use client";

import type { DefinitionSummary, WorkflowDefinition } from "@/stores/workflowsStore";
import { cn } from "@/lib/utils";
import { LOGIC_TYPES } from "@/lib/workflow/nodeClassification";
import React from "react";
interface Position {
  x: number;
  y: number;
}
interface LayoutNode {
  id: string;
  type: string;
  nodeType: "trigger" | "action" | "logic";
  position: Position;
  children: string[];
}
interface LayoutEdge {
  id: string;
  source: string;
  target: string;
  branch?: "then" | "else" | "resumed" | "timed_out";
}

// Node type color mapping - matches actual editor node colors
const NODE_COLORS = {
  trigger: {
    fill: "#2450FF",
    // B3 Blue (matches TriggerNode icon color)
    stroke: "#1a3fd9" // Darker B3 Blue
  },
  action: {
    fill: "#22c55e",
    // green-500 (matches ActionNode icon color)
    stroke: "#16a34a" // green-600
  },
  logic: {
    fill: "#a855f7",
    // purple-500 (matches LogicNode icon color)
    stroke: "#7c3aed" // violet-600
  }
} as const;

// Trigger types - explicit set to avoid false positives from substring matching
const TRIGGER_TYPES = new Set(["manual-trigger", "schedule-trigger", "webhook-trigger", "price-trigger", "price-alert-trigger", "blockchain-event-trigger", "contract-event-trigger"]);
function getNodeCategory(type: string): "trigger" | "action" | "logic" {
  if (TRIGGER_TYPES.has(type)) {
    return "trigger";
  }
  if (LOGIC_TYPES.has(type)) {
    return "logic";
  }
  return "action";
}

// Layout constants — proportional to real canvas nodes (280×~80px, ~3.5:1 ratio)
const NODE_WIDTH = 52;
const NODE_HEIGHT = 16;
const H_SPACING = 20;
const V_SPACING = 16;

/**
 * Auto-layout nodes in a tree structure if positions aren't available
 */
function autoLayoutNodes(nodes: Record<string, {
  children?: string[];
  loopBody?: string[];
}>): Map<string, Position> {
  const positions = new Map<string, Position>();

  // Find root node (node with no parent)
  const childIds = new Set<string>();
  Object.values(nodes).forEach(node => {
    node.children?.forEach(childId => childIds.add(childId));
    node.loopBody?.forEach(childId => childIds.add(childId));
  });
  let rootId: string | null = null;
  for (const nodeId of Object.keys(nodes)) {
    if (!childIds.has(nodeId)) {
      rootId = nodeId;
      break;
    }
  }
  if (!rootId) {
    // Fallback: use first node as root
    rootId = Object.keys(nodes)[0];
  }
  const visited = new Set<string>();
  const levels: string[][] = [];
  function bfs(startId: string) {
    const queue: Array<{
      id: string;
      level: number;
    }> = [{
      id: startId,
      level: 0
    }];
    while (queue.length > 0) {
      const {
        id,
        level
      } = queue.shift()!;
      if (visited.has(id)) continue;
      visited.add(id);
      if (!levels[level]) {
        levels[level] = [];
      }
      levels[level].push(id);
      const node = nodes[id];
      if (node?.children) {
        for (const childId of node.children) {
          if (!visited.has(childId) && nodes[childId]) {
            queue.push({
              id: childId,
              level: level + 1
            });
          }
        }
      }
      if (node?.loopBody) {
        for (const ocId of node.loopBody) {
          if (!visited.has(ocId) && nodes[ocId]) {
            queue.push({
              id: ocId,
              level: level + 1
            });
          }
        }
      }
    }
  }
  if (rootId) {
    bfs(rootId);
  }

  // Position nodes by level
  let maxWidth = 0;
  levels.forEach(level => {
    maxWidth = Math.max(maxWidth, level.length);
  });
  const totalWidth = maxWidth * (NODE_WIDTH + H_SPACING);
  levels.forEach((level, levelIndex) => {
    const levelWidth = level.length * NODE_WIDTH + (level.length - 1) * H_SPACING;
    const startX = (totalWidth - levelWidth) / 2;
    level.forEach((nodeId, nodeIndex) => {
      positions.set(nodeId, {
        x: startX + nodeIndex * (NODE_WIDTH + H_SPACING),
        y: levelIndex * (NODE_HEIGHT + V_SPACING)
      });
    });
  });
  return positions;
}

/**
 * Convert workflow definition to layout data
 * @param compact - Always use auto-layout regardless of stored positions (for card previews)
 */
function buildLayoutData(definition: MinimapDefinition, compact = false): {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
} {
  const nodes: LayoutNode[] = [];
  const edges: LayoutEdge[] = [];
  if (!definition?.nodes) {
    return {
      nodes,
      edges
    };
  }

  // Check if we have stored positions (skip in compact mode — always auto-layout)
  const hasStoredPositions = !compact && Object.values(definition.nodes).some(node => node.position?.x !== undefined);

  // Auto-layout if no stored positions or compact mode
  const positions = hasStoredPositions ? null : autoLayoutNodes(definition.nodes);
  for (const [nodeId, node] of Object.entries(definition.nodes)) {
    const position = hasStoredPositions && node.position ? node.position : positions?.get(nodeId) || {
      x: 0,
      y: 0
    };
    nodes.push({
      id: nodeId,
      type: node.type,
      nodeType: getNodeCategory(node.type),
      position,
      children: node.children || []
    });

    // Create edges
    if (node.children) {
      for (const childId of node.children) {
        const childNode = definition.nodes[childId];
        edges.push({
          id: `${nodeId}-${childId}`,
          source: nodeId,
          target: childId,
          branch: childNode?.branch
        });
      }
    }
    if (node.loopBody) {
      for (const ocId of node.loopBody) {
        edges.push({
          id: `${nodeId}-${ocId}`,
          source: nodeId,
          target: ocId
        });
      }
    }
  }
  return {
    nodes,
    edges
  };
}

/**
 * Calculate the bounding box of all nodes
 */
function calculateBounds(nodes: LayoutNode[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  if (nodes.length === 0) {
    return {
      minX: 0,
      minY: 0,
      maxX: 100,
      maxY: 100
    };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const node of nodes) {
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + NODE_WIDTH);
    maxY = Math.max(maxY, node.position.y + NODE_HEIGHT);
  }
  return {
    minX,
    minY,
    maxX,
    maxY
  };
}

/** Accepts both full WorkflowDefinition and slim DefinitionSummary. */
type MinimapDefinition = WorkflowDefinition | DefinitionSummary;
interface WorkflowMinimapProps {
  definition: MinimapDefinition;
  /** Width of the minimap container */
  width?: number;
  /** Height of the minimap container */
  height?: number;
  /** Additional class names */
  className?: string;
  /** Show node count badge */
  showNodeCount?: boolean;
  /** Border radius */
  rounded?: "sm" | "md" | "lg" | "xl";
  /** Force compact auto-layout (ignores stored positions — for card previews) */
  compact?: boolean;
}
export function WorkflowMinimap({
  definition,
  width = 120,
  height = 80,
  className,
  showNodeCount = false,
  rounded = "lg",
  compact = false
}: WorkflowMinimapProps) {
  const uid = React.useId().replace(/:/g, "");
  const {
    nodes,
    edges
  } = React.useMemo(() => buildLayoutData(definition, compact), [definition, compact]);
  if (nodes.length === 0) {
    return <div className={cn("relative flex items-center justify-center overflow-hidden", "from-ds-neutral-100 to-ds-neutral-200 dark:from-ds-neutral-900 bg-gradient-to-br dark:to-zinc-950", "border-ds-neutral-300 dark:border-ds-neutral-700/50 border", "text-ds-text-disabled dark:text-ds-text-muted", rounded === "sm" && "rounded-sm", rounded === "md" && "rounded-md", rounded === "lg" && "rounded-lg", rounded === "xl" && "rounded-xl", className)} style={{
      width,
      height
    }}>
        {/* Dotted background with angled fade */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(#d1d5db_1px,transparent_1px)] opacity-70 [background-size:10px_10px] [mask-image:linear-gradient(135deg,white_50%,transparent_85%)] dark:bg-[radial-gradient(#3f3f46_1px,transparent_1px)]" aria-hidden="true" />
        <span className="relative z-10 text-xs">Empty</span>
      </div>;
  }
  const bounds = calculateBounds(nodes);
  const H_PAD = 16;
  const V_PAD = 24;

  // Calculate scale to fit everything in the viewport
  const contentWidth = bounds.maxX - bounds.minX + H_PAD * 2;
  const contentHeight = bounds.maxY - bounds.minY + V_PAD * 2;
  const scaleX = width / contentWidth;
  const scaleY = height / contentHeight;
  const scale = Math.min(scaleX, scaleY, 1.2); // Cap scale to prevent too much zoom

  // SVG viewBox
  const viewBoxWidth = width / scale;
  const viewBoxHeight = height / scale;
  const viewBoxX = bounds.minX - H_PAD - (viewBoxWidth - contentWidth) / 2;
  const viewBoxY = bounds.minY - V_PAD - (viewBoxHeight - contentHeight) / 2;

  // Build a position map for edge drawing
  const positionMap = new Map(nodes.map(n => [n.id, n.position]));
  return <div className={cn("group relative overflow-hidden", "from-ds-neutral-100 to-ds-neutral-200 dark:from-ds-neutral-900 bg-gradient-to-br dark:to-zinc-950", "border-ds-neutral-300 dark:border-ds-neutral-700/50 border", rounded === "sm" && "rounded-sm", rounded === "md" && "rounded-md", rounded === "lg" && "rounded-lg", rounded === "xl" && "rounded-xl", className)} style={{
    width,
    height
  }}>
      {/* Dotted background with angled fade */}
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(#d1d5db_1px,transparent_1px)] opacity-70 transition-opacity duration-300 [background-size:10px_10px] [mask-image:linear-gradient(135deg,white_50%,transparent_85%)] group-hover:opacity-90 dark:bg-[radial-gradient(#3f3f46_1px,transparent_1px)]" aria-hidden="true" />
      <svg width={width} height={height} viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`} className="relative z-10 block">
        {/* Glow filter definitions - IDs scoped per instance to avoid conflicts */}
        <defs>
          <filter id={`glow-trigger-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={`glow-action-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={`glow-logic-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Edges */}
        <g className="edges">
          {edges.map(edge => {
          const sourcePos = positionMap.get(edge.source);
          const targetPos = positionMap.get(edge.target);
          if (!sourcePos || !targetPos) return null;

          // Calculate edge points (from bottom of source to top of target)
          const x1 = sourcePos.x + NODE_WIDTH / 2;
          const y1 = sourcePos.y + NODE_HEIGHT;
          const x2 = targetPos.x + NODE_WIDTH / 2;
          const y2 = targetPos.y;

          // Curved path
          const midY = (y1 + y2) / 2;
          return <path key={edge.id} d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`} fill="none" className={edge.branch === "else" ? "stroke-zinc-400 dark:stroke-slate-500" : "stroke-zinc-500 dark:stroke-slate-400"} strokeWidth={2.5} strokeLinecap="round" strokeDasharray={edge.branch === "else" ? "5,4" : undefined} opacity={0.85} />;
        })}
        </g>

        {/* Nodes */}
        <g className="nodes">
          {nodes.map(node => {
          const colors = NODE_COLORS[node.nodeType];
          const filterId = `glow-${node.nodeType}-${uid}`;
          return <g key={node.id} transform={`translate(${node.position.x}, ${node.position.y})`}>
                {/* Glow background */}
                <rect width={NODE_WIDTH} height={NODE_HEIGHT} rx={6} ry={6} fill={colors.fill} opacity={0.35} filter={`url(#${filterId})`} />
                {/* Main node */}
                <rect width={NODE_WIDTH} height={NODE_HEIGHT} rx={6} ry={6} fill={colors.fill} stroke={colors.stroke} strokeWidth={2} />
              </g>;
        })}
        </g>
      </svg>

      {/* Node count badge */}
      {showNodeCount && nodes.length > 0 && <div className="bg-ds-neutral-900/70 dark:text-ds-text-primary text-3xs absolute bottom-1 right-1 rounded px-1.5 py-0.5 font-medium text-white dark:bg-white/80">
          {nodes.length} nodes
        </div>}
    </div>;
}